import React, { FC, MouseEvent } from 'react';
import { Box, IconButton, TableCell, TableRow, Typography } from '@mui/material';
import InvoiceItemType from './invoice-item-type/invoice-item-type.component';
import InvoiceItemNumberStatus from './invoice-item-number-status/invoice-item-number-status.component';
import { useTranslation } from 'react-i18next';
import { Invoice, InvoiceDelivery, InvoiceItemContextEvent, InvoiceType, Type } from 'models/invoice';
import { formatCurrency, formatDate } from 'utils/format';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import InvoiceCertificationStatus from './invoice-certification-status/invoice-certification-status.component';
import DebitCreditStatus from './debit-credit-status/debit-credit-status.component';
import { Order } from 'models/order';

type InvoiceItemProps = {
    invoice: Invoice;
    isTechnician: boolean;
    isCertificationEnabled: boolean;
    onInvoiceClick: (invoiceId: string) => void;
    onInvoiceContextMenu: (event: InvoiceItemContextEvent) => void;
};

const InvoiceItem: FC<InvoiceItemProps> = ({
    invoice,
    isTechnician,
    isCertificationEnabled,
    onInvoiceClick,
    onInvoiceContextMenu,
}: InvoiceItemProps) => {
    const { t: translate } = useTranslation(['invoices', 'orders']);
    const handleContextMenu = (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        onInvoiceContextMenu({ invoice: invoice, mouseX: event.clientX, mouseY: event.clientY });
    };
    return (
        <TableRow
            sx={{
                cursor: 'pointer',
            }}
            onClick={() => onInvoiceClick(invoice._id)}
            hover
        >
            <TableCell>
                <DebitCreditStatus isCredit={invoice.type === Type.CREDIT} />
            </TableCell>
            {isCertificationEnabled && !isTechnician ? (
                <TableCell>
                    <InvoiceCertificationStatus
                        lineItemWithOrderCount={invoice.lineItemWithOrderCount || 1}
                        certifiedOrderCount={
                            invoice.invoiceType === InvoiceType.SINGLE
                                ? (invoice.order as Order)?.isCertified
                                    ? 1
                                    : 0
                                : invoice.certifiedOrderCount || 0
                        }
                    />
                </TableCell>
            ) : undefined}
            <TableCell>{invoice.invoiceDate ? <div>{formatDate(invoice.invoiceDate, 'date')}</div> : '-'}</TableCell>
            <TableCell>{invoice.dueDate ? <div>{formatDate(invoice.dueDate, 'date')}</div> : '-'}</TableCell>
            <TableCell>
                <InvoiceItemNumberStatus
                    isCredited={invoice.isCredited || false}
                    pdfUrl={invoice.pdfUrl}
                    invoiceNumber={invoice.invoiceNumber}
                    status={invoice.status}
                    creditInvoices={invoice.creditInvoices || []}
                />
            </TableCell>
            {isTechnician ? (
                <TableCell sx={{ maxWidth: 270 }}>
                    <div>{invoice.deliveryAddress?.name}</div>
                    {invoice.deliveryAddress?.name !== invoice.customer?.name ? (
                        <Typography variant="body2" color="grey.700">
                            {invoice.customer?.name}
                        </Typography>
                    ) : undefined}
                </TableCell>
            ) : (
                <TableCell sx={{ maxWidth: 270 }}>{invoice.supplier?.name}</TableCell>
            )}
            {invoice.customer?.reference ? (
                <TableCell>{invoice.customer?.reference}</TableCell>
            ) : (
                <TableCell>-</TableCell>
            )}
            <TableCell>
                <InvoiceItemType
                    invoiceType={translate(`${invoice.invoiceType || InvoiceType.SINGLE}`)}
                    deliveryMethod={translate(`${invoice.deliveryMethod || InvoiceDelivery.ELECTRONIC_BILLING}`)}
                />
            </TableCell>
            <TableCell>
                {invoice.sentDate ? (
                    <Box>{formatDate(invoice.sentDate, 'date')}</Box>
                ) : (
                    <Box
                        sx={{
                            whiteSpace: 'nowrap',
                            color: '#888',
                        }}
                    >
                        <div>{translate('NOT_SENT')}</div>
                    </Box>
                )}
            </TableCell>
            <TableCell align="right" sx={{ maxWidth: 110 }}>
                {/* TODO: Shared component */}
                <Box>{formatCurrency(invoice.total?.total || 0, invoice.total.currency)}</Box>
                {invoice.type === Type.DEBIT ? (
                    <Box
                        sx={{
                            whiteSpace: 'nowrap',
                        }}
                    >
                        <Typography variant="caption">
                            {translate('orders:VAT')}: {formatCurrency(invoice.total?.vat || 0, invoice.total.currency)}
                        </Typography>
                    </Box>
                ) : undefined}
            </TableCell>
            <TableCell sx={{ paddingRight: 0 }}>
                <IconButton size="small" onClick={handleContextMenu}>
                    <MoreVertIcon />
                </IconButton>
            </TableCell>
        </TableRow>
    );
};

export default InvoiceItem;
